'use client';

import * as React from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from "@/lib/utils";
import { useUploadImage } from '@/lib/api/images';

const MIME_IMAGE = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const NON_SVG_IMAGE_MIME = MIME_IMAGE.join(',');

interface ImageUploadProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  url: string | undefined | null;
  onImageAvailable: (url: string) => void;
  disabled?: boolean;
}

export const ImageUpload = ({
  url: originalUrl,
  onImageAvailable,
  className,
  disabled,
  ...innerProps
}: ImageUploadProps) => {
  const { mutate: uploadImage, isPending, isError, error } = useUploadImage();
  const [dragActive, setDragActive] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleFile = (file: File) => {
    if (file && MIME_IMAGE.includes(file.type)) {
      const formData = new FormData();
      formData.append('file', file);
      uploadImage(formData, {
        onError: (error) => {
          console.error(error);
        },
        onSuccess: (data) => {
          onImageAvailable(data.file.url);
        },
      });
    }
  };

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    const { files } = event.target as HTMLInputElement;
    const file = files?.item(0);
    if (file) handleFile(file);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  const handleClear = () => {
    onImageAvailable('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  return (
    <div className={cn('flex items-start gap-2', className)}>
      {/* Image Preview */}
      {originalUrl && !isPending && (
        <div className="relative flex-shrink-0">
          <div className="border-border h-16 w-16 overflow-hidden rounded-md border">
            <img src={originalUrl} alt="Preview" className="h-full w-full object-cover" />
          </div>
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -right-1 -top-1 h-5 w-5 rounded-full"
            onClick={handleClear}
            disabled={disabled || isPending}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Upload Area */}
      <div className="flex-1">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={cn(
            'border-border hover:border-primary relative flex cursor-pointer items-center gap-2 rounded-md border border-dashed p-2 transition-colors',
            dragActive && 'border-primary bg-primary/5',
            isPending && 'opacity-50',
            (disabled || isPending) && 'cursor-not-allowed',
          )}
          onClick={() => !disabled && !isPending && inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={NON_SVG_IMAGE_MIME}
            disabled={isPending || disabled}
            onInput={handleInput}
            className="hidden"
            {...innerProps}
          />

          {isPending ? (
            <>
              <div className="bg-primary h-8 w-8 animate-pulse rounded-md" />
              <div className="flex-1">
                <p className="text-foreground text-xs font-medium">Uploading...</p>
                <p className="text-muted-foreground text-[10px]">Please wait</p>
              </div>
            </>
          ) : (
            <>
              <div className="bg-muted flex h-8 w-8 items-center justify-center rounded-md">
                {originalUrl ? (
                  <ImageIcon className="text-muted-foreground h-4 w-4" />
                ) : (
                  <Upload className="text-muted-foreground h-4 w-4" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-foreground text-xs font-medium">
                  {originalUrl ? 'Change image' : 'Upload image'}
                </p>
                <p className="text-muted-foreground text-[10px]">JPG, PNG, GIF or WebP</p>
              </div>
            </>
          )}
        </div>

        {/* Error State */}
        {isError && <p className="text-destructive mt-1 text-[10px]">{error.message || 'Upload failed'}</p>}
      </div>
    </div>
  );
};

export default ImageUpload;
